import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateTask, createTask, addComment } from "../../../Redux/slices/TaskSlice";
import { IoClose, IoCalendarOutline, IoPersonOutline, IoSend } from "react-icons/io5";

const TaskDetailModal = ({ task, onClose, conversationId }) => {
    const dispatch = useDispatch();
    const userId = localStorage.getItem("userId");

    // Selectors
    // Try to get participants from chat state - might need adjustment based on real Redux structure
    const currentChat = useSelector(state => state.chatUser.chatData);
    const participants = currentChat?.users || [];

    // Local State
    const [title, setTitle] = useState(task.title || "");
    const [description, setDescription] = useState(task.description || "");
    const [dueDate, setDueDate] = useState(task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : "");
    const [assignedUserId, setAssignedUserId] = useState(task.assignedUserId?._id || "");
    const [commentText, setCommentText] = useState("");

    const isNew = !!task.isNew;

    const handleSave = async () => {
        if (!title.trim()) return;

        const taskData = {
            title,
            description,
            dueDate: dueDate || null,
            assignedUserId: assignedUserId || null,
            conversationId: conversationId // Ensure we have the ID
        };

        if (isNew) {
            await dispatch(createTask(taskData));
        } else {
            await dispatch(updateTask({ taskId: task._id, updates: taskData }));
        }
        onClose();
    };

    const handleAccept = () => {
        dispatch(updateTask({ taskId: task._id, updates: { assignmentStatus: 'accepted' } }));
        onClose();
    };

    const handleDecline = () => {
        dispatch(updateTask({ taskId: task._id, updates: { assignmentStatus: 'declined', assignedUserId: null } }));
        onClose();
    };

    const handleSendComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim()) return;

        await dispatch(addComment({ taskId: task._id, text: commentText }));
        setCommentText("");
    };

    // Auto-save on blur or specific actions could be added, but manual "Save" is safer for v1

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="font-semibold text-lg text-gray-800">{isNew ? "New Task" : "Task Details"}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                        <IoClose size={24} className="text-gray-500" />
                    </button>
                </div>

                {/* Assignment Banner */}
                {!isNew && task.assignedUserId?._id === userId && task.assignmentStatus === 'pending' && (
                    <div className="bg-indigo-50 p-4 px-6 flex items-center justify-between border-b border-indigo-100">
                        <p className="text-sm text-indigo-800 font-medium">You have been assigned this task.</p>
                        <div className="flex gap-2">
                            <button onClick={handleDecline} className="text-xs px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-100">Decline</button>
                            <button onClick={handleAccept} className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm">Accept Task</button>
                        </div>
                    </div>
                )}

                {/* Content Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Title */}
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Task Title"
                        className="w-full text-xl font-bold text-gray-800 placeholder-gray-300 border-none focus:ring-0 p-0"
                    />

                    {/* Meta Controls */}
                    <div className="flex flex-col gap-4">
                        {/* Assignee */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                <IoPersonOutline />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Assign To</label>
                                <select
                                    value={assignedUserId}
                                    onChange={(e) => setAssignedUserId(e.target.value)}
                                    className="w-full text-sm text-gray-700 bg-transparent border-none focus:ring-0 p-0 cursor-pointer"
                                >
                                    <option value="">Unassigned</option>
                                    {participants.map(user => (
                                        <option key={user._id} value={user._id}>
                                            {user.name || user.userName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Due Date */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                <IoCalendarOutline />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Due Date</label>
                                <input
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-full text-sm text-gray-700 bg-transparent border-none focus:ring-0 p-0"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add a description..."
                            rows={4}
                            className="w-full text-sm text-gray-700 bg-gray-50 border border-transparent focus:bg-white focus:border-indigo-200 rounded-xl p-3 transition-all resize-none"
                        />
                    </div>

                    {/* Comments / Activity */}
                    {!isNew && (
                        <div className="pt-6 border-t border-gray-100">
                            <h4 className="text-sm font-semibold text-gray-800 mb-4">Activity & Comments</h4>
                            <div className="space-y-4 mb-4">
                                {task.comments?.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic">No comments yet.</p>
                                ) : (
                                    task.comments?.map((comment, idx) => (
                                        <div key={idx} className="flex gap-2">
                                            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-600 flex-shrink-0">
                                                {comment.userId?.name?.[0] || 'U'}
                                            </div>
                                            <div className="bg-gray-50 rounded-lg rounded-tl-none p-2 px-3">
                                                <div className="flex items-baseline justify-between gap-4 mb-1">
                                                    <span className="text-xs font-semibold text-gray-700">{comment.userId?.name}</span>
                                                    <span className="text-[10px] text-gray-400">{new Date(comment.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                                <p className="text-xs text-gray-600">{comment.text}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <form onSubmit={handleSendComment} className="relative">
                                <input
                                    type="text"
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    placeholder="Write a comment..."
                                    className="w-full pl-4 pr-10 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:border-indigo-300 focus:bg-white transition-all"
                                />
                                <button type="submit" disabled={!commentText.trim()} className="absolute right-1.5 top-1.5 p-1.5 bg-indigo-600 text-white rounded-full disabled:opacity-50 hover:bg-indigo-700 transition-colors">
                                    <IoSend size={12} />
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2 text-sm font-medium bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 shadow-sm transition-all"
                    >
                        {isNew ? "Create Task" : "Save Changes"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskDetailModal;
